import { parseColor, rgbToHex } from './colorUtils'

/**
 * Computes relative luminance of an RGB color
 * Formula from WCAG 2.1: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((channel) => {
    const sRGB = channel / 255
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Calculates contrast ratio between two luminance values
 */
function getContrastRatio(lum1: number, lum2: number): number {
  const lighter = Math.max(lum1, lum2)
  const darker = Math.min(lum1, lum2)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Converts hex to HSL
 */
function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const rgb = parseColor(hex)
  if (!rgb) return { h: 0, s: 0, l: 50 }

  const r = rgb.r / 255
  const g = rgb.g / 255
  const b = rgb.b / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const diff = max - min

  let h = 0
  let s = 0
  let l = (max + min) / 2

  if (diff !== 0) {
    s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / diff + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / diff + 2) / 6
        break
      case b:
        h = ((r - g) / diff + 4) / 6
        break
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  }
}

/**
 * Converts HSL to hex
 */
function hslToHex(h: number, s: number, l: number): string {
  h = h / 360
  s = s / 100
  l = l / 100

  let r, g, b

  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q

    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Finds an outline color that preserves the button's hue while meeting
 * contrast requirements against the BUTTON background
 */
function findAccessibleOutlineColorWithHue(
  buttonColor: string,
  buttonLuminance: number,
  minContrast: number = 3.0
): string {
  const hsl = hexToHSL(buttonColor)
  
  // If button is grayscale (low saturation), use neutral grays
  if (hsl.s < 15) {
    for (let gray = 0; gray <= 255; gray += 1) {
      const testLuminance = getRelativeLuminance(gray, gray, gray)
      const buttonContrast = getContrastRatio(testLuminance, buttonLuminance)
      
      if (buttonContrast >= minContrast) {
        return rgbToHex(gray, gray, gray)
      }
    }
  }
  
  let bestColor: string | null = null
  let bestSaturation = -1
  
  // Start from original saturation and work down to find most vibrant valid color
  const maxSat = Math.min(hsl.s + 10, 100)
  
  for (let sat = maxSat; sat >= 0; sat -= 5) {
    // Search all lightness values from original outward
    const lightnessValues: number[] = []
    
    // Add original lightness first
    lightnessValues.push(hsl.l)
    
    // Then alternate lighter and darker
    for (let offset = 1; offset <= 100; offset++) {
      if (hsl.l + offset <= 100) lightnessValues.push(hsl.l + offset)
      if (hsl.l - offset >= 0) lightnessValues.push(hsl.l - offset)
    }
    
    for (const lightness of lightnessValues) {
      const testColor = hslToHex(hsl.h, sat, lightness)
      const testRgb = parseColor(testColor)
      if (!testRgb) continue
      
      const testLuminance = getRelativeLuminance(testRgb.r, testRgb.g, testRgb.b)
      const buttonContrast = getContrastRatio(testLuminance, buttonLuminance)
      
      // Check contrast against BUTTON background
      if (buttonContrast >= minContrast) {
        if (sat > bestSaturation) {
          bestColor = testColor
          bestSaturation = sat
        }
        break // Move to next saturation level
      }
    }
    
    // Stop early if we found a reasonably saturated color
    if (bestSaturation >= Math.max(30, hsl.s * 0.5)) {
      break
    }
  }

  if (bestColor) return bestColor

  // Final fallback: use grayscale
  for (let gray = 0; gray <= 255; gray += 1) {
    const testLuminance = getRelativeLuminance(gray, gray, gray)
    const buttonContrast = getContrastRatio(testLuminance, buttonLuminance)
    
    if (buttonContrast >= minContrast) {
      const color = rgbToHex(gray, gray, gray)
      return color
    }
  }
  
  return '#000000'
}

/**
 * Generates an accessible focus outline color based on button background
 * Ensures at least 3:1 contrast ratio against the BUTTON background
 * Preserves the button's color hue when possible
 * 
 * @param buttonBgColor - The button's background color (hex, rgb, or rgba format)
 * @param minContrast - Minimum contrast ratio (default 3.0 for UI components)
 * @returns Hex color string for the focus outline
 */
export function getFocusOutlineColor(
  buttonBgColor: string,
  minContrast: number = 3.0
): string {
  const buttonRgb = parseColor(buttonBgColor)
  
  if (!buttonRgb) {
    return '#404040'
  }

  const buttonLuminance = getRelativeLuminance(buttonRgb.r, buttonRgb.g, buttonRgb.b)

  const outlineColor = findAccessibleOutlineColorWithHue(
    buttonBgColor,
    buttonLuminance,
    minContrast
  )
  
  return outlineColor
}
